/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useContext } from 'react';
import { isEmpty, findIndex } from 'lodash';
import { useParams } from 'react-router';
import config from 'config';
import { GlobalTypesContext, AssetContext } from 'context';
import { handleApiError, parametersApi } from 'api';
import startCase from 'utils/start-case';
import validateAssetName from 'utils/validate-asset-name';

const useAddNewAsset = (
  setOpenNewAsset,
  node,
  isNewAssetOpen,
  projectLevel,
  setExpanded
) => {
  const {
    getAssetTypeTree,
    assetTypeTrees,
    hiddenAssetSubTypes,
    hiddenAssetTypes,
  } = useContext(GlobalTypesContext);
  const [openSnackBar, setOpenSnackBar] = useState({
    open: false,
    variant: '',
  });
  const { projectId } = useParams();
  const { setAllAssets, allAssets, getAssets } = useContext(AssetContext);
  const [assetTypeOptions, setAssetTypeOptions] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [assetSubTypesLoading, setAssetSubTypesLoading] = useState(false);
  const [assetSubTypeOptions, setAssetSubTypeOptions] = useState([]);
  const [assetTypeValue, setAssetTypeValue] = useState(null);
  const [names, setNames] = useState([]);
  const [error, setError] = useState(null);

  const getAssetTypeInfo = async () => {
    let typeOptions = [];
    if (node.assetType?.id) {
      const response = await getAssetTypeTree([node?.assetType?.id]);
      if (response && !isEmpty(response[0].children)) {
        typeOptions.push(
          ...response[0].children
            .filter(child => {
              return !hiddenAssetTypes.includes(child.id);
            })
            .map(child => ({
              id: child.id,
              label: startCase(child.name),
              value: child.id,
            }))
        );
      }
    }
    if (projectLevel) {
      typeOptions.push(
        ...assetTypeTrees
          .filter(child => {
            return !hiddenAssetTypes.includes(child.id);
          })
          .map(child => ({
            id: child.id,
            label: startCase(child.name),
            value: child.id,
          }))
      );
    }
    if (config.featureFlags.isNullTypeAssetPostEnabled) {
      typeOptions.push({
        id: 'unassigned',
        label: 'Unassigned',
        value: 'unassigned',
      });
    }
    if (!typeOptions.length) {
      setError({
        msg: 'Not Available',
        details: 'No asset types available',
      });
    }
    setAssetTypeOptions(typeOptions);
    setTypesLoading(false);
  };

  const getAssetChildren = id => {
    const selected = allAssets.find(asset => asset.id === id);
    const chunkSize = 99;
    if (!projectLevel && selected) {
      Array.from(
        { length: Math.ceil(selected.children.length / chunkSize) },
        (_, i) =>
          getAssets({
            asset_id: selected.children.slice(
              i * chunkSize,
              i * chunkSize + chunkSize
            ),
          })
      );
    }
  };

  useEffect(() => {
    if (isNewAssetOpen) {
      getAssetTypeInfo();
      getAssetChildren(node.id);
    }
  }, [node.id]);

  const getSubTypes = async after => {
    setAssetSubTypesLoading(true);
    const query = {
      asset_type_id: assetTypeValue,
      sort_by: 'name',
      order: 'asc',
    };
    if (after) query.after = after;
    try {
      const response = await parametersApi('getAssetSubTypes', query);
      const { assetSubTypes, paging } = response;
      if (assetSubTypes) {
        const filteredSubTypes = assetSubTypes?.filter(
          subType => !hiddenAssetSubTypes.includes(subType.id)
        );
        setAssetSubTypeOptions(curr => [...curr, ...filteredSubTypes]);
      }
      if (paging?.cursors?.after) {
        await getSubTypes(paging?.cursors?.after);
      } else {
        setAssetSubTypesLoading(false);
      }
    } catch (err) {
      setAssetSubTypesLoading(false);
      setError(err.response?.data);
    }
  };

  useEffect(() => {
    setAssetSubTypeOptions([]);
    setError(null);
    if (assetTypeValue && assetTypeValue !== 'unassigned') {
      getSubTypes();
    }
  }, [assetTypeValue]);

  const postNewAsset = async values => {
    const chunkSize = 99;
    const newAssets = values.assetNames.map(name => {
      const newAsset = {
        asset_type_id:
          values.assetType === 'unassigned' ? null : values.assetType,
        name,
        parent_id: node.id,
        project_id: projectId,
      };
      if (values.assetSubType) newAsset.asset_sub_type_id = values.assetSubType;
      return newAsset;
    });
    const chunks = Array.from(
      { length: Math.ceil(newAssets.length / chunkSize) },
      (_, i) => newAssets.slice(i * chunkSize, i * chunkSize + chunkSize)
    );

    try {
      for (const chunk of chunks) {
        const response = await parametersApi('postAsset', {
          body: { assets: chunk },
        });
        if (response) {
          const { assets } = response;
          setAllAssets(curr => {
            let parentIndex = -1;
            parentIndex = findIndex(curr, item => {
              return item.id === assets[0].parent;
            });
            if (parentIndex !== -1)
              curr[parentIndex].children.unshift(...assets.map(({ id }) => id));
            curr.unshift(...assets);
          });
          setExpanded(curr => [...curr, assets[0].parent]);
        }
      }
      setOpenSnackBar({
        open: true,
        variant: 'success',
      });
    } catch (error) {
      setOpenSnackBar({
        open: true,
        variant: 'error',
        message: error?.response?.data.details || error.message,
      });
      handleApiError(error, []);
    } finally {
      setOpenNewAsset(false);
    }
    return true;
  };

  const validateAddAssetForm = async values => {
    const errors = {};

    if (!values.assetType) {
      errors.assetType = 'Asset Type is required';
    }

    if (assetSubTypeOptions?.length && !values?.assetSubType) {
      errors.assetSubType = 'Asset Sub Type is required';
    }

    if (
      values.assetType &&
      (values?.assetNames?.length > 0 || values?.assetName)
    ) {
      const namesArray = values?.assetNames ? [...values?.assetNames] : [];
      if (values?.assetName) namesArray.push(values?.assetName);
      const errorArray = namesArray.map(name => {
        return validateAssetName(
          name,
          projectLevel ? null : node.id,
          values?.assetType === 'unassigned' ? null : values.assetType,
          values?.assetSubType || null,
          allAssets
        );
      });
      const nameErr = errorArray.filter(error => error !== null);
      if (nameErr.length > 0) {
        errors.assetName = nameErr[0];
      }
    }
    if (
      (!values.assetName && values?.assetNames?.length === 0) ||
      !values?.assetNames
    ) {
      errors.assetName = 'Asset Name is required';
    }
    return errors;
  };

  return {
    postNewAsset,
    openSnackBar,
    assetTypeOptions,
    typesLoading,
    assetTypeValue,
    setAssetTypeValue,
    assetSubTypesLoading,
    setAssetSubTypesLoading,
    assetSubTypeOptions,
    setAssetSubTypeOptions,
    error,
    names,
    setNames,
    validateAddAssetForm,
  };
};

export default useAddNewAsset;
